import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form } from 'react-bootstrap';
import { FaEdit, FaTrashAlt } from 'react-icons/fa';
import axios from 'axios';
import './BannersPage.css';

function BannersPage() {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFullImageModal, setShowFullImageModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [newBanner, setNewBanner] = useState({
    title: '',
    description: '',
    image_url: null,
  });
  const [updatedBanner, setUpdatedBanner] = useState({
    title: '',
    description: '',
    image_url: null,
  });
  const [bannersData, setBannersData] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [fullImage, setFullImage] = useState(null);

  useEffect(() => {
    axios.get('https://agpipefitting.org.et/api/banners')
      .then((response) => {
        console.log('API Response:', response.data);
        const banners = response.data?.data || response.data || [];
        setBannersData(banners);
      })
      .catch((error) => {
        console.error('Error fetching banners:', error);
        setErrorMessage('Failed to load banners. Please try again.');
      });
  }, []);

  const handleShowEditModal = (bannerId) => {
    const banner = bannersData.find((item) => item.id === bannerId);
    if (banner) {
      setEditingBanner(banner);
      setUpdatedBanner({ ...banner });
      setShowEditModal(true);
    }
  };

  

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingBanner(null);
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleShowAddModal = () => setShowAddModal(true);
  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setNewBanner({ title: '', description: '', image_url: null });
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleImageChange = (e, isEdit = false) => {
    const file = e.target.files[0];
    isEdit 
      ? setUpdatedBanner({ ...updatedBanner, image_url: file })
      : setNewBanner({ ...newBanner, image_url: file });
  };

  const handleAddBanner = (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('title', newBanner.title);
    formData.append('description', newBanner.description);
    formData.append('image_url', newBanner.image_url);

    axios.post('https://agpipefitting.org.et/api/banners', formData)
      .then((response) => {
        setBannersData([...bannersData, response.data.banner]);
        setSuccessMessage('Banner added successfully!');
        handleCloseAddModal();
      })
      .catch((error) => {
        setErrorMessage('Error adding banner: ' + error.message);
      });
  };

  const handleUpdateBanner = (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('_method', 'PUT'); // Add this line
    formData.append('title', updatedBanner.title);
    formData.append('description', updatedBanner.description);
    if (updatedBanner.image_url instanceof File) {
      formData.append('image_url', updatedBanner.image_url);
    }
  
    // Change axios.put to axios.post
    axios.post(`https://agpipefitting.org.et/api/banners/${editingBanner.id}`, formData)
      .then((response) => {
        const updatedBanners = bannersData.map((item) =>
          item.id === editingBanner.id ? response.data.banner : item
        );
        setBannersData(updatedBanners);
        setSuccessMessage('Banner updated successfully!');
        handleCloseEditModal();
      })
      .catch((error) => {
        setErrorMessage('Error updating banner: ' + error.message);
      });
  };

  const handleDeleteBanner = (bannerId) => {
    axios.delete(`https://agpipefitting.org.et/api/banners/${bannerId}`)
      .then(() => {
        setBannersData(bannersData.filter((item) => item.id !== bannerId));
        setSuccessMessage('Banner deleted successfully!');
      })
      .catch((error) => {
        setErrorMessage('Error deleting banner: ' + error.message);
      });
  };

  const handleShowFullImage = (imageUrl) => {
    const fullUrl = imageUrl.startsWith('http') 
      ? imageUrl
      : `https://agpipefitting.org.et/storage/${imageUrl}`;
    setFullImage(fullUrl);
    setShowFullImageModal(true);
  };

  const handleCloseFullImageModal = () => {
    setShowFullImageModal(false);
    setFullImage(null);
  };

  return (
    <div className="banners-page">
      <div className="page-header">
        <h1>Banners</h1>
        <Button variant="primary" onClick={handleShowAddModal}>
          Add Banner
        </Button>
      </div>

      {successMessage && <div className="alert alert-success">{successMessage}</div>}
      {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}

      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Title</th>
            <th>Description</th>
            <th>Image</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {bannersData.map((banner) => (
            <tr key={banner.id}>
              <td>{banner.title}</td>
              <td>{banner.description}</td>
              <td>
                <img
                  src={banner.image_url.startsWith('http') 
                    ? banner.image_url 
                    : `https://agpipefitting.org.et/storage/${banner.image_url}`}
                  alt={banner.title}
                  style={{ width: '100px', cursor: 'pointer' }}
                  onError={(e) => (e.target.src = '/fallback-image.jpg')}
                  onClick={() => handleShowFullImage(banner.image_url)}
                />
              </td>
              <td>
                <Button variant="warning" onClick={() => handleShowEditModal(banner.id)}>
                  <FaEdit />
                </Button>{' '}
                <Button variant="danger" onClick={() => handleDeleteBanner(banner.id)}>
                  <FaTrashAlt />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Add Banner Modal */}
      <Modal show={showAddModal} onHide={handleCloseAddModal}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Banner</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddBanner}>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                value={newBanner.title}
                onChange={(e) => setNewBanner({ ...newBanner, title: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newBanner.description}
                onChange={(e) => setNewBanner({ ...newBanner, description: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Image</Form.Label>
              <Form.Control
                type="file"
                onChange={(e) => handleImageChange(e)}
                required
              />
            </Form.Group>
            <div className="modal-footer">
              <Button variant="secondary" onClick={handleCloseAddModal}>
                Close
              </Button>
              <Button variant="primary" type="submit">
                Save Changes
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

     {/* Edit Banner Modal */}
<Modal show={showEditModal} onHide={handleCloseEditModal} backdrop="static">
  <Modal.Header closeButton>
    <Modal.Title>Edit Banner</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    <Form onSubmit={handleUpdateBanner}>
      {/* Title Input */}
      <Form.Group className="mb-3">
        <Form.Label>Title</Form.Label>
        <Form.Control
          type="text"
          value={updatedBanner.title}
          onChange={(e) => setUpdatedBanner({ ...updatedBanner, title: e.target.value })}
          required
        />
      </Form.Group>

      {/* Description Input */}
      <Form.Group className="mb-3">
        <Form.Label>Description</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={updatedBanner.description}
          onChange={(e) => setUpdatedBanner({ ...updatedBanner, description: e.target.value })}
        />
      </Form.Group>

      {/* Image Upload */}
      <Form.Group className="mb-3">
        <Form.Label>Image</Form.Label>
        <Form.Control type="file" accept="image/*" onChange={(e) => handleImageChange(e, true)} />
        
        {/* Image Preview */}
        {updatedBanner.image_url && (
          <div className="mt-2">
            <strong>Current Image:</strong>
            <div className="mt-2">
              <img
                src={typeof updatedBanner.image_url === "string" ? updatedBanner.image_url : URL.createObjectURL(updatedBanner.image_url)}
                alt="Banner Preview"
                style={{ width: "100%", maxHeight: "200px", objectFit: "cover", borderRadius: "5px" }}
              />
            </div>
          </div>
        )}
      </Form.Group>

      {/* Modal Footer Buttons */}
      <div className="modal-footer">
        <Button variant="secondary" onClick={handleCloseEditModal}>
          Close
        </Button>
        <Button variant="primary" type="submit">
          Update Banner
        </Button>
      </div>
    </Form>
  </Modal.Body>
</Modal>


      {/* Full Image Modal */}
      <Modal show={showFullImageModal} onHide={handleCloseFullImageModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Full Size Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {fullImage && (
            <img
              src={fullImage}
              alt="Full Banner Preview"
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default BannersPage;